import { Check, cn } from "@app/ui";

import type { I18nKey } from "../../lib/language.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import type { MultiDayFocus } from "../../stores/settings.store.js";

const OPTIONS: { value: MultiDayFocus; labelKey: I18nKey }[] = [
  { value: "current_week", labelKey: "general.multiDayFocus.currentWeek" },
  { value: "current_day", labelKey: "general.multiDayFocus.currentDay" },
  { value: "current_day_minus_one", labelKey: "general.multiDayFocus.previousDay" },
];

const REFERENCE_TODAY = 15;

function startDayFor(value: MultiDayFocus, weekStartsOn: 0 | 1): number {
  if (value === "current_week") return weekStartsOn === 0 ? 12 : 13;
  if (value === "current_day_minus_one") return REFERENCE_TODAY - 1;
  return REFERENCE_TODAY;
}

function FocusPreview({ value, weekStartsOn }: { value: MultiDayFocus; weekStartsOn: 0 | 1 }) {
  const locale = useAppLocale();
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "narrow" });
  const startDay = startDayFor(value, weekStartsOn);
  const days = Array.from({ length: 7 }, (_, index) => new Date(2026, 6, startDay + index));

  return (
    <div aria-hidden="true" className="overflow-hidden rounded-md border bg-background shadow-sm">
      <div className="h-2 border-b bg-muted/70" />
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const isToday = day.getDate() === REFERENCE_TODAY;
          return (
            <div
              key={day.toISOString()}
              className={cn(
                "relative flex min-w-0 flex-col items-center gap-1 border-r px-0.5 py-2 last:border-r-0",
                index === 0 && "bg-muted/60",
                isToday && "bg-primary/10",
              )}
            >
              {index === 0 && <span className="absolute inset-x-0 top-0 h-0.5 bg-primary" />}
              <span className="text-[calc(9px*var(--app-text-scale))] font-medium uppercase text-muted-foreground">
                {weekday.format(day)}
              </span>
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[calc(10px*var(--app-text-scale))] font-semibold",
                  isToday && "bg-primary text-primary-foreground",
                )}
              >
                {day.getDate()}
              </span>
              <span className={cn("h-6 w-full rounded-sm bg-muted/60", isToday && "bg-primary/20")} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MultiDayFocusPicker({
  value,
  weekStartsOn,
  onChange,
}: {
  value: MultiDayFocus;
  weekStartsOn: 0 | 1;
  onChange: (value: MultiDayFocus) => void;
}) {
  const { t } = useI18n();

  return (
    <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <legend className="sr-only">{t("general.multiDayFocus")}</legend>
      {OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <label
            key={option.value}
            className={cn(
              "relative cursor-pointer rounded-lg border p-2.5 transition-colors hover:bg-accent/50",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring",
              selected && "border-primary bg-primary/5 ring-1 ring-primary",
            )}
          >
            <input
              type="radio"
              name="multi-day-focus"
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <FocusPreview value={option.value} weekStartsOn={weekStartsOn} />
            <span className="mt-2 flex min-h-5 items-center justify-between gap-2 text-sm font-medium">
              {t(option.labelKey)}
              {selected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
